import { useQuery } from '@tanstack/react-query';

import {
  brokers,
  type BankAccount,
  type FinancialContact,
} from '@/shared/api/resources';
import type { ApiError } from '@/shared/api/client';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';

import { fieldErrorOf } from '../shared/fieldError';

import { dicaDadosBancarios } from './contaBancaria';

const tiposDeConta = [
  { value: 'corrente', label: 'Conta corrente' },
  { value: 'poupanca', label: 'Conta poupanca' },
];

/**
 * Dados financeiros da corretora: a conta que recebe o repasse e o responsavel
 * financeiro.
 *
 * O mesmo bloco serve ao cadastro da seguradora e a tela da propria corretora. A
 * conta vem inteira ou nao vem: banco, tipo, agencia e conta juntos, e o servidor
 * recusa a conta pela metade apontando o campo que falta.
 *
 * A conta tem alcada propria (corretores.dados_bancarios.update). Sem ela, a conta
 * fica somente leitura e o responsavel financeiro continua editavel: so o destino
 * do repasse tem dono proprio.
 */
export function FinancialDataFields({
  conta,
  responsavel,
  erro,
  contaSomenteLeitura = false,
  onContaChange,
  onResponsavelChange,
}: {
  conta: BankAccount;
  responsavel: FinancialContact;
  erro: ApiError | null;
  contaSomenteLeitura?: boolean | undefined;
  onContaChange: (conta: BankAccount) => void;
  onResponsavelChange: (responsavel: FinancialContact) => void;
}) {
  function setConta(campo: keyof BankAccount, valor: string) {
    onContaChange({ ...conta, [campo]: valor });
  }

  // O banco vem do catalogo da plataforma (Core/GetBanks do OnPoint), com o codigo e
  // o nome. Sem o catalogo, o campo volta a ser o codigo digitado, e a conta continua
  // podendo ser salva.
  const bancos = useQuery({
    queryKey: ['catalogs', 'bank-codes'],
    queryFn: ({ signal }) => brokers.bankCodes(signal),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
  const opcoesDeBanco = (bancos.data ?? []).map((banco) => ({
    value: banco.compe_code,
    label: `${banco.compe_code} - ${banco.name}`,
  }));
  if (
    conta.bank_code &&
    !opcoesDeBanco.some((opcao) => opcao.value === conta.bank_code)
  ) {
    opcoesDeBanco.unshift({
      value: conta.bank_code,
      label: `${conta.bank_code} (como gravado)`,
    });
  }

  function setResponsavel(campo: keyof FinancialContact, valor: string) {
    onResponsavelChange({ ...responsavel, [campo]: valor });
  }

  return (
    <div className="flex flex-col gap-5">
      <fieldset disabled={contaSomenteLeitura} className="flex flex-col gap-4">
        <legend className="text-xs font-semibold text-ink-700">Dados bancarios</legend>
        {contaSomenteLeitura ? (
          <p className="text-2xs text-ink-500">{dicaDadosBancarios}</p>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          {bancos.data && bancos.data.length > 0 ? (
            <Select
              label="Banco"
              placeholder="Selecione o banco"
              options={opcoesDeBanco}
              value={conta.bank_code}
              error={fieldErrorOf(erro, 'bank_account.bank_code')}
              onChange={(event) => setConta('bank_code', event.target.value)}
            />
          ) : (
            <Field
              label="Banco"
              inputMode="numeric"
              maxLength={3}
              placeholder="341"
              value={conta.bank_code}
              error={fieldErrorOf(erro, 'bank_account.bank_code')}
              hint="Codigo de tres digitos do banco na compensacao."
              onChange={(event) => setConta('bank_code', event.target.value)}
            />
          )}
          <Select
            label="Tipo de conta"
            placeholder="Nao informado"
            options={tiposDeConta}
            value={conta.account_type}
            error={fieldErrorOf(erro, 'bank_account.account_type')}
            onChange={(event) => setConta('account_type', event.target.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-[1fr_6rem_1fr_6rem]">
          <Field
            label="Agencia"
            inputMode="numeric"
            maxLength={5}
            value={conta.agency}
            error={fieldErrorOf(erro, 'bank_account.agency')}
            onChange={(event) => setConta('agency', event.target.value)}
          />
          <Field
            label="Digito da agencia"
            maxLength={1}
            value={conta.agency_digit}
            error={fieldErrorOf(erro, 'bank_account.agency_digit')}
            onChange={(event) => setConta('agency_digit', event.target.value)}
          />
          <Field
            label="Conta"
            inputMode="numeric"
            maxLength={20}
            value={conta.account}
            error={fieldErrorOf(erro, 'bank_account.account')}
            onChange={(event) => setConta('account', event.target.value)}
          />
          <Field
            label="Digito da conta"
            maxLength={1}
            value={conta.account_digit}
            error={fieldErrorOf(erro, 'bank_account.account_digit')}
            onChange={(event) => setConta('account_digit', event.target.value)}
          />
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-4">
        <legend className="text-xs font-semibold text-ink-700">
          Responsavel financeiro
        </legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Nome completo"
            value={responsavel.name}
            error={fieldErrorOf(erro, 'financial_contact.name')}
            onChange={(event) => setResponsavel('name', event.target.value)}
          />
          <Field
            label="CPF"
            placeholder="000.000.000-00"
            value={responsavel.document}
            error={fieldErrorOf(erro, 'financial_contact.document')}
            onChange={(event) => setResponsavel('document', event.target.value)}
          />
          <Field
            label="E-mail do responsavel"
            type="email"
            value={responsavel.email}
            error={fieldErrorOf(erro, 'financial_contact.email')}
            onChange={(event) => setResponsavel('email', event.target.value)}
          />
          <Field
            label="Telefone para contato"
            value={responsavel.phone}
            error={fieldErrorOf(erro, 'financial_contact.phone')}
            onChange={(event) => setResponsavel('phone', event.target.value)}
          />
        </div>
      </fieldset>
    </div>
  );
}
